import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { organizationUsageTextV822 } from '../CustomerVertexLogV811/organizationUsageV822';
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Modal,
  Pagination,
  Table,
} from 'semantic-ui-react';

import {
  API,
  copy,
  isRoot,
  showError,
  showSuccess,
  showWarning,
} from '../../helpers';
import PageSizeSelectV8, {
  readPageSizeV8,
} from '../../components/PageSizeSelectV8';
import ResizableHeaderCellV815 from '../../components/ResizableHeaderCellV815';
import useResizableColumnsV815 from '../../hooks/useResizableColumnsV815';
import {
  formatImagePixelsV97,
  imagePixelAttributesV97,
} from '../../components/imagePixelsV97';
import { applyUserInputErrorFilterV820 } from '../../components/userInputErrorFilterV820';
import {
  buildVertexDetailCopyTextV815,
  formatNamedIdentityV815,
} from './detailFormatV815';
import './VertexLogV83.css';

const COLUMN_WIDTHS_V815 = Object.freeze({
  time: 164,
  provider: 230,
  request: 190,
  organization: 160,
  user: 120,
  model: 190,
  project: 170,
  key: 150,
  location: 150,
  outcome: 104,
  latency: 150,
  quality: 132,
  detail: 88,
});

const formatTime = (value) =>
  value ? new Date(Number(value) * 1000).toLocaleString() : '-';

const formatDuration = (value, zeroAsDash = false) => {
  const ms = Number(value) || 0;
  if (zeroAsDash && ms <= 0) return '-';
  if (ms < 1000) return ms + ' ms';
  return (ms / 1000).toFixed(2) + ' s';
};

const providerLabelV816 = (providerFamily, t) =>
  providerFamily === 'google_ai_studio'
    ? t('vertex_log.provider_google_ai_studio_v816')
    : t('vertex_log.provider_vertex_ai_v816');

const ProviderAccessCellV817 = ({ item, t }) => {
  const providerIdentity = formatNamedIdentityV815(
    item?.channel_name_snapshot,
    item?.channel_id
  );
  return (
    <div className='vertex-log-provider-access-v817'>
      <Label
        basic
        color={item?.provider_family === 'google_ai_studio' ? 'teal' : 'blue'}
      >
        {providerLabelV816(item?.provider_family, t)}
      </Label>
      <span
        className='vertex-log-provider-access-identity-v817'
        title={providerIdentity}
      >
        {providerIdentity}
      </span>
    </div>
  );
};

const DetailFactV815 = ({ label, children, mono = false }) => (
  <div>
    <span>{label}</span>
    {mono ? (
      <code>{children || '-'}</code>
    ) : (
      <div className='vertex-log-detail-value-v815'>{children || '-'}</div>
    )}
  </div>
);

const CredentialCell = ({ keyIndex, keyID, fingerprint }) => (
  <div className='vertex-log-key-v815'>
    <span>
      #{keyIndex || '-'} / ID {keyID || '-'}
    </span>
    <code>{fingerprint || '-'}</code>
  </div>
);

const QualityCell = ({ item, t }) => (
  <div className='vertex-log-label-stack-v815'>
    {item.has_missing_usage ? (
      <Label color='red'>{t('vertex_log.quality_missing')}</Label>
    ) : null}
    {item.has_fallback_usage ? (
      <Label color='orange'>{t('vertex_log.quality_fallback')}</Label>
    ) : null}
    {!item.has_missing_usage && !item.has_fallback_usage ? (
      <Label basic color='green'>
        {item.usage_source || 'reported'}
      </Label>
    ) : null}
  </div>
);

const ProviderDiagnosticV815 = ({ item, t }) => {
  if (!item?.error_status && !item?.error_reason && !item?.error_message) {
    return null;
  }
  const facts = [
    [t('vertex_log.error_domain_v815'), item.error_domain],
    [t('vertex_log.error_service_v815'), item.error_service],
    [t('vertex_log.error_permission_v815'), item.error_permission],
    [t('vertex_log.error_resource_v815'), item.error_resource],
  ].filter(([, value]) => value);
  return (
    <Message negative className='vertex-log-provider-error-v815'>
      <Message.Header>
        {t('vertex_log.provider_diagnostic_v815')}:{' '}
        {[item.error_status, item.error_reason].filter(Boolean).join(' / ') ||
          item.error_code ||
          '-'}
      </Message.Header>
      {item.error_message ? <p>{item.error_message}</p> : null}
      {facts.length > 0 ? (
        <div className='vertex-log-provider-facts-v815'>
          {facts.map(([label, value]) => (
            <span key={label}>
              <strong>{label}:</strong> <code>{value}</code>
            </span>
          ))}
        </div>
      ) : null}
      {item.error_console_url ? (
        <a
          href={item.error_console_url}
          target='_blank'
          rel='noopener noreferrer'
        >
          <Icon name='external alternate' />
          {t('vertex_log.open_console_v815')}
        </a>
      ) : null}
    </Message>
  );
};

const QueuePanelV815 = ({ snapshot, loading, t }) => (
  <div className='vertex-queue-view-v815'>
    <div className='vertex-queue-summary-v815'>
      <Label basic>
        {t('vertex_log.queue_waiting_v815')}: {snapshot.queue?.length || 0}
      </Label>
      <Label basic>
        {t('vertex_log.queue_active_v815')}: {snapshot.active?.length || 0}
      </Label>
      <span>
        {t('vertex_log.queue_updated_v815')}:{' '}
        {formatTime(snapshot.generated_at)}
      </span>
    </div>
    {loading ? (
      <Loader active inline='centered' />
    ) : (
      <>
        <Header as='h3'>{t('vertex_log.active_slots_v815')}</Header>
        {(snapshot.active || []).length === 0 ? (
          <Message info content={t('vertex_log.no_active_slots_v815')} />
        ) : (
          <div className='vertex-log-table-scroll-v83'>
            <Table compact celled unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {t('vertex_log.kind_v815')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>{t('vertex_log.channel')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('vertex_log.project')}</Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('vertex_log.actual_key_v815')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('vertex_log.capacity_v815')}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(snapshot.active || []).map((slot) => (
                  <Table.Row
                    key={[
                      slot.candidate?.channel_id,
                      slot.candidate?.channel_key_id,
                      slot.kind,
                    ].join('-')}
                  >
                    <Table.Cell>{slot.kind}</Table.Cell>
                    <Table.Cell>
                      #{slot.candidate?.channel_id || '-'}
                    </Table.Cell>
                    <Table.Cell>{slot.candidate?.project_id || '-'}</Table.Cell>
                    <Table.Cell>
                      <CredentialCell
                        keyIndex={slot.candidate?.channel_key_index}
                        keyID={slot.candidate?.channel_key_id}
                        fingerprint={slot.candidate?.credential_fingerprint}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      {slot.active} /{' '}
                      {slot.limit === 0
                        ? t('vertex_log.unlimited_v815')
                        : slot.limit}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}

        <Header as='h3'>{t('vertex_log.waiting_requests_v815')}</Header>
        {(snapshot.queue || []).length === 0 ? (
          <Message info content={t('vertex_log.no_waiting_requests_v815')} />
        ) : (
          <div className='vertex-log-table-scroll-v83'>
            <Table compact celled unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {t('vertex_log.position_v815')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('vertex_log.request_id')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('vertex_log.organization')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>{t('vertex_log.user')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('vertex_log.model')}</Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('vertex_log.kind_v815')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('vertex_log.wait_v815')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('vertex_log.candidates_v815')}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(snapshot.queue || []).map((entry) => (
                  <Table.Row key={entry.queue_id}>
                    <Table.Cell>#{entry.position}</Table.Cell>
                    <Table.Cell>
                      <code>{entry.request_id || '-'}</code>
                    </Table.Cell>
                    <Table.Cell>{entry.organization_name || '-'}</Table.Cell>
                    <Table.Cell>
                      {formatNamedIdentityV815(entry.username, entry.user_id)}
                    </Table.Cell>
                    <Table.Cell>{entry.model_name || '-'}</Table.Cell>
                    <Table.Cell>{entry.kind}</Table.Cell>
                    <Table.Cell>
                      {formatDuration(entry.wait_ms)}
                      <div>
                        {t('vertex_log.heartbeats_v815')}:{' '}
                        {entry.heartbeat_count || 0}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div className='vertex-queue-candidates-v815'>
                        {(entry.candidates || []).map((candidate) => (
                          <div
                            key={candidate.channel_key_id}
                            title={candidate.credential_fingerprint || ''}
                          >
                            {candidate.project_id || '-'} / #
                            {candidate.channel_key_index || '-'} /{' '}
                            {candidate.active}/
                            {candidate.limit === 0
                              ? t('vertex_log.unlimited_v815')
                              : candidate.limit}
                          </div>
                        ))}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </>
    )}
  </div>
);

const DetailModalV815 = ({ detail, loading, onClose, t }) => {
  const groupedItems = useMemo(() => {
    const grouped = {};
    (detail?.items || []).forEach((item) => {
      const key = item.attempt_id == null ? 'request' : String(item.attempt_id);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(item);
    });
    return grouped;
  }, [detail]);

  const request = detail?.request || {};
  const qualitySummary = [
    request.has_missing_usage ? t('vertex_log.quality_missing') : '',
    request.has_fallback_usage ? t('vertex_log.quality_fallback') : '',
  ]
    .filter(Boolean)
    .join(' / ');
  const copyDetail = async () => {
    if (await copy(buildVertexDetailCopyTextV815(detail))) {
      showSuccess(t('vertex_log.copy_success_v815'));
    } else {
      showWarning(t('vertex_log.copy_failed_v815'));
    }
  };
  return (
    <Modal
      open={!!detail}
      onClose={onClose}
      size='large'
      className='vertex-log-detail-modal-v83'
    >
      <Modal.Header className='vertex-log-detail-header-v815'>
        <span>{t('vertex_log.detail_title')}</span>
        <code>{request.request_id || '-'}</code>
      </Modal.Header>
      <Modal.Content scrolling>
        {loading ? (
          <Loader active inline='centered' />
        ) : detail ? (
          <>
            <Header as='h3' className='vertex-log-detail-section-title-v815'>
              {t('vertex_log.request_overview_v815')}
            </Header>
            <div className='vertex-log-detail-grid-v83'>
              <DetailFactV815 label={t('vertex_log.log_id_v815')} mono>
                {request.id ? '#' + request.id : '-'}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.request_id')} mono>
                {request.request_id}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.provider_v816')}>
                {providerLabelV816(request.provider_family, t)}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.organization')}>
                {formatNamedIdentityV815(
                  request.organization_name_snapshot,
                  request.billing_account_id
                )}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.user')}>
                {formatNamedIdentityV815(
                  request.username_snapshot,
                  request.user_id
                )}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.requested_model_v815')} mono>
                {request.requested_model}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.billed_model_v815')} mono>
                {request.billed_model}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.access_provider_v817')}>
                {formatNamedIdentityV815(
                  request.channel_name_snapshot,
                  request.channel_id
                )}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.project')} mono>
                {request.project_reference}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.actual_key_v815')}>
                <CredentialCell
                  keyIndex={request.final_channel_key_index}
                  keyID={request.final_channel_key_id}
                  fingerprint={request.credential_fingerprint}
                />
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.region_publisher_v815')}>
                {request.region || '-'} / {request.publisher || '-'}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.service_tier')}>
                {request.service_tier}
              </DetailFactV815>
            </div>

            <Header as='h3' className='vertex-log-detail-section-title-v815'>
              {t('vertex_log.timing_quality_v815')}
            </Header>
            <div className='vertex-log-detail-grid-v83'>
              <DetailFactV815 label={t('vertex_log.created_at_v815')}>
                {formatTime(request.created_at)}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.started_at_v815')}>
                {formatTime(request.started_at)}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.finished_at_v815')}>
                {formatTime(request.finished_at)}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.outcome')}>
                {request.outcome || '-'} / HTTP {request.http_status || '-'}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.final_attempt_v815')}>
                {request.final_attempt_no
                  ? '#' + request.final_attempt_no
                  : '-'}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.error_code_v815')} mono>
                {request.error_code}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.elapsed')}>
                {formatDuration(request.elapsed_ms)}
              </DetailFactV815>
              <DetailFactV815 label='TTFT / First Event'>
                {formatDuration(request.first_response_ms, true)}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.queue_wait_v815')}>
                {formatDuration(request.queue_wait_ms)}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.usage_source_v815')} mono>
                {request.usage_source}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.quality_flags_v815')}>
                {qualitySummary || request.usage_source || '-'}
              </DetailFactV815>
              <DetailFactV815 label={t('vertex_log.purpose_v815')} mono>
                {request.purpose}
              </DetailFactV815>
            </div>

            <ProviderDiagnosticV815 item={request} t={t} />

            {(detail.attempts || []).map((attempt) => (
              <section className='vertex-log-attempt-v83' key={attempt.id}>
                <Header as='h4'>
                  {t('vertex_log.attempt', { number: attempt.attempt_no })}
                </Header>
                <div className='vertex-log-detail-grid-v83 vertex-log-attempt-grid-v815'>
                  <DetailFactV815 label={t('vertex_log.attempt_id_v815')} mono>
                    {attempt.id ? '#' + attempt.id : '-'}
                  </DetailFactV815>
                  <DetailFactV815 label={t('vertex_log.provider_v816')}>
                    {providerLabelV816(attempt.provider_family, t)}
                  </DetailFactV815>
                  <DetailFactV815 label={t('vertex_log.access_provider_v817')}>
                    {formatNamedIdentityV815(
                      attempt.channel_name_snapshot,
                      attempt.channel_id
                    )}
                  </DetailFactV815>
                  <DetailFactV815 label={t('vertex_log.project')} mono>
                    {attempt.project_reference}
                  </DetailFactV815>
                  <DetailFactV815 label={t('vertex_log.actual_key_v815')} mono>
                    #{attempt.channel_key_index || '-'} / ID{' '}
                    {attempt.channel_key_id || '-'} /{' '}
                    {attempt.credential_fingerprint || '-'}
                  </DetailFactV815>
                  <DetailFactV815
                    label={t('vertex_log.provider_request_id_v815')}
                    mono
                  >
                    {attempt.provider_request_id}
                  </DetailFactV815>
                  <DetailFactV815 label={t('vertex_log.started_at_v815')}>
                    {formatTime(attempt.started_at)}
                  </DetailFactV815>
                  <DetailFactV815 label={t('vertex_log.finished_at_v815')}>
                    {formatTime(attempt.finished_at)}
                  </DetailFactV815>
                  <DetailFactV815 label={t('vertex_log.outcome')}>
                    {attempt.outcome || '-'} / HTTP {attempt.http_status || '-'}
                  </DetailFactV815>
                  <DetailFactV815 label={t('vertex_log.elapsed')}>
                    {formatDuration(attempt.elapsed_ms)}
                  </DetailFactV815>
                  <DetailFactV815 label='TTFT / First Event'>
                    {formatDuration(attempt.first_response_ms, true)}
                  </DetailFactV815>
                  <DetailFactV815 label={t('vertex_log.queue_wait_v815')}>
                    {formatDuration(attempt.queue_wait_ms)}
                  </DetailFactV815>
                  <DetailFactV815
                    label={t('vertex_log.usage_source_v815')}
                    mono
                  >
                    {attempt.usage_source}
                  </DetailFactV815>
                  <DetailFactV815 label={t('vertex_log.error_code_v815')} mono>
                    {attempt.error_code}
                  </DetailFactV815>
                </div>
                <ProviderDiagnosticV815 item={attempt} t={t} />
                <Table compact celled unstackable>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>
                        {t('vertex_log.meter')}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t('vertex_log.quantity')}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t('vertex_log.unit')}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t('vertex_log.measurement')}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t('vertex_log.source')}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t('vertex_log.attributes')}
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {(groupedItems[String(attempt.id)] || []).map((item) => (
                      <Table.Row key={item.id}>
                        <Table.Cell>{item.meter}</Table.Cell>
                        <Table.Cell>
                          {item.quantity == null ? (
                            <Label basic color='red'>
                              NULL
                            </Label>
                          ) : (
                            String(item.quantity)
                          )}
                        </Table.Cell>
                        <Table.Cell>{item.unit}</Table.Cell>
                        <Table.Cell>{item.measurement_status}</Table.Cell>
                        <Table.Cell>{item.source || '-'}</Table.Cell>
                        <Table.Cell>
                          {item.meter === 'image_dimensions' ? (
                            <span style={{ overflowWrap: 'anywhere' }}>
                              {formatImagePixelsV97(
                                imagePixelAttributesV97(item.attributes_json),
                                t('common.unknown', { defaultValue: 'Unknown' })
                              )}
                            </span>
                          ) : (
                            <code>{item.attributes_json || '{}'}</code>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                    {(groupedItems[String(attempt.id)] || []).length === 0 ? (
                      <Table.Row>
                        <Table.Cell colSpan='6'>
                          {t('vertex_log.no_meters')}
                        </Table.Cell>
                      </Table.Row>
                    ) : null}
                  </Table.Body>
                </Table>
              </section>
            ))}
          </>
        ) : null}
      </Modal.Content>
      <Modal.Actions>
        <Button
          icon
          labelPosition='left'
          onClick={copyDetail}
          disabled={!detail || loading}
        >
          <Icon name='copy outline' />
          {t('vertex_log.copy_all_v815')}
        </Button>
        <Button onClick={onClose}>{t('vertex_log.close')}</Button>
      </Modal.Actions>
    </Modal>
  );
};

const VertexLogV83 = () => {
  const { t } = useTranslation();
  const [view, setView] = useState('logs');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    readPageSizeV8('v83-page-size-vertex-logs', 20)
  );
  const [loading, setLoading] = useState(false);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const emptyFilters = {
    request_id: '',
    billing_account_id: '',
    model_name: '',
    provider_family: '',
    outcome: '',
    quality: '',
    user_input_error_only: false,
  };
  const [filters, setFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [queueSnapshot, setQueueSnapshot] = useState({
    generated_at: 0,
    active: [],
    queue: [],
  });
  const [queueLoading, setQueueLoading] = useState(false);
  const {
    widths: columnWidths,
    startResize,
    resetColumn,
  } = useResizableColumnsV815(
    COLUMN_WIDTHS_V815,
    'alfredapi.vertex_log.column_widths.v8.15'
  );

  const outcomeOptions = useMemo(
    () => [
      { key: 'all', value: '', text: t('vertex_log.filter_all') },
      {
        key: 'success',
        value: 'success',
        text: t('vertex_log.outcome_success'),
      },
      {
        key: 'failure',
        value: 'failure',
        text: t('vertex_log.outcome_failed'),
      },
      {
        key: 'canceled',
        value: 'canceled',
        text: t('vertex_log.outcome_canceled'),
      },
    ],
    [t]
  );
  const providerOptions = useMemo(
    () => [
      { key: 'all', value: '', text: t('vertex_log.filter_all') },
      {
        key: 'vertex',
        value: 'vertex',
        text: t('vertex_log.provider_vertex_ai_v816'),
      },
      {
        key: 'google_ai_studio',
        value: 'google_ai_studio',
        text: t('vertex_log.provider_google_ai_studio_v816'),
      },
    ],
    [t]
  );
  const qualityOptions = useMemo(
    () => [
      { key: 'all', value: '', text: t('vertex_log.filter_all') },
      {
        key: 'missing',
        value: 'missing',
        text: t('vertex_log.quality_missing'),
      },
      {
        key: 'fallback',
        value: 'fallback',
        text: t('vertex_log.quality_fallback'),
      },
      {
        key: 'provider',
        value: 'provider',
        text: t('vertex_log.quality_provider'),
      },
    ],
    [t]
  );
  const columns = useMemo(
    () => [
      { key: 'time', label: t('vertex_log.time') },
      { key: 'provider', label: t('vertex_log.access_provider_v817') },
      { key: 'request', label: t('vertex_log.request_id') },
      { key: 'organization', label: t('vertex_log.organization') },
      { key: 'user', label: t('vertex_log.user') },
      { key: 'model', label: t('vertex_log.model') },
      { key: 'project', label: t('vertex_log.project') },
      { key: 'key', label: t('vertex_log.actual_key_v815') },
      { key: 'location', label: t('vertex_log.location_tier_v815') },
      { key: 'outcome', label: t('vertex_log.outcome') },
      { key: 'latency', label: t('vertex_log.latency_v815') },
      { key: 'quality', label: t('vertex_log.quality') },
      { key: 'detail', label: t('vertex_log.detail') },
    ],
    [t]
  );
  const tableWidth = columns.reduce(
    (sum, column) => sum + columnWidths[column.key],
    0
  );

  useEffect(() => {
    if (!isRoot()) return;
    let active = true;
    API.get('/api/log/organizations')
      .then((response) => {
        if (!active || !response?.data?.success) return;
        setOrganizationOptions([
          {
            key: 'all',
            value: '',
            text: t('vertex_log.all_organizations_v815'),
          },
          ...(response.data.data || []).map((organization) => ({
            key: String(organization.id),
            value: String(organization.id),
            text: organization.name || '#' + organization.id,
          })),
        ]);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [t]);

  const buildQuery = useCallback(() => {
    const query = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    [
      'request_id',
      'billing_account_id',
      'model_name',
      'provider_family',
      'outcome',
    ].forEach((key) => {
      if (appliedFilters[key]) query.set(key, appliedFilters[key]);
    });
    if (appliedFilters.quality === 'missing') {
      query.set('has_missing_usage', 'true');
    } else if (appliedFilters.quality === 'fallback') {
      query.set('has_fallback_usage', 'true');
    } else if (appliedFilters.quality === 'provider') {
      query.set('has_missing_usage', 'false');
      query.set('has_fallback_usage', 'false');
    }
    return applyUserInputErrorFilterV820(
      query,
      appliedFilters.user_input_error_only
    ).toString();
  }, [appliedFilters, page, pageSize]);

  const loadLogs = useCallback(async () => {
    if (!isRoot()) return;
    setLoading(true);
    try {
      const response = await API.get('/api/vertex-log/?' + buildQuery());
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || t('vertex_log.errors.load'));
      }
      const data = response.data.data || {};
      setItems(data.items || []);
      setTotal(Number(data.total || 0));
    } catch (error) {
      showError(error.message || t('vertex_log.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [buildQuery, t]);

  const loadQueue = useCallback(
    async (quiet = false) => {
      if (!isRoot()) return;
      if (!quiet) setQueueLoading(true);
      try {
        const response = await API.get('/api/vertex-queue/');
        if (!response?.data?.success) {
          throw new Error(
            response?.data?.message || t('vertex_log.errors.queue')
          );
        }
        setQueueSnapshot(
          response.data.data || { generated_at: 0, active: [], queue: [] }
        );
      } catch (error) {
        if (!quiet) showError(error.message || t('vertex_log.errors.queue'));
      } finally {
        if (!quiet) setQueueLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (view === 'logs') loadLogs();
  }, [loadLogs, view]);

  useEffect(() => {
    if (view !== 'queue') return undefined;
    loadQueue();
    const timer = window.setInterval(() => loadQueue(true), 5000);
    return () => window.clearInterval(timer);
  }, [loadQueue, view]);

  const openDetail = async (item) => {
    setDetailLoading(true);
    setDetail({ request: item, attempts: [], items: [] });
    try {
      const response = await API.get('/api/vertex-log/' + item.id);
      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message || t('vertex_log.errors.detail')
        );
      }
      setDetail(response.data.data);
    } catch (error) {
      setDetail(null);
      showError(error.message || t('vertex_log.errors.detail'));
    } finally {
      setDetailLoading(false);
    }
  };

  if (!isRoot()) {
    return (
      <Message
        negative
        header={t('vertex_log.root_only')}
        content={t('vertex_log.root_only_description')}
      />
    );
  }

  return (
    <div className='dashboard-container management-container-v4 vertex-log-page-v83'>
      <div className='vertex-log-header-v83'>
        <div>
          <Header as='h2'>{t('vertex_log.title')}</Header>
          <p>{t('vertex_log.description_v815')}</p>
          <p>{organizationUsageTextV822(t, 'provider_description')}</p>
        </div>
        <Button
          icon
          title={t('vertex_log.refresh')}
          aria-label={t('vertex_log.refresh')}
          onClick={() => (view === 'logs' ? loadLogs() : loadQueue())}
          loading={view === 'logs' ? loading : queueLoading}
        >
          <Icon name='refresh' />
        </Button>
      </div>

      <Button.Group compact className='vertex-log-view-switch-v815'>
        <Button active={view === 'logs'} onClick={() => setView('logs')}>
          <Icon name='list alternate outline' />
          {t('vertex_log.requests_view_v815')}
        </Button>
        <Button active={view === 'queue'} onClick={() => setView('queue')}>
          <Icon name='tasks' />
          {t('vertex_log.queue_view_v815')}
          {(queueSnapshot.queue || []).length > 0 ? (
            <Label circular color='orange'>
              {queueSnapshot.queue.length}
            </Label>
          ) : null}
        </Button>
      </Button.Group>

      {view === 'queue' ? (
        <QueuePanelV815 snapshot={queueSnapshot} loading={queueLoading} t={t} />
      ) : (
        <>
          <Form className='vertex-log-filters-v83'>
            <Form.Group widths='equal'>
              <Form.Input
                label={t('vertex_log.request_id')}
                value={filters.request_id}
                onChange={(_, { value }) =>
                  setFilters((current) => ({ ...current, request_id: value }))
                }
              />
              <Form.Field>
                <label>{t('vertex_log.organization')}</label>
                <Dropdown
                  selection
                  search
                  clearable
                  fluid
                  options={organizationOptions}
                  value={filters.billing_account_id}
                  placeholder={t('vertex_log.all_organizations_v815')}
                  onChange={(_, { value }) =>
                    setFilters((current) => ({
                      ...current,
                      billing_account_id: value,
                    }))
                  }
                />
              </Form.Field>
              <Form.Input
                label={t('vertex_log.model')}
                value={filters.model_name}
                onChange={(_, { value }) =>
                  setFilters((current) => ({ ...current, model_name: value }))
                }
              />
              <Form.Field>
                <label>{t('vertex_log.provider_v816')}</label>
                <Dropdown
                  selection
                  fluid
                  options={providerOptions}
                  value={filters.provider_family}
                  onChange={(_, { value }) =>
                    setFilters((current) => ({
                      ...current,
                      provider_family: value,
                    }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>{t('vertex_log.outcome')}</label>
                <Dropdown
                  selection
                  fluid
                  options={outcomeOptions}
                  value={filters.outcome}
                  onChange={(_, { value }) =>
                    setFilters((current) => ({ ...current, outcome: value }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>{t('vertex_log.quality')}</label>
                <Dropdown
                  selection
                  fluid
                  options={qualityOptions}
                  value={filters.quality}
                  onChange={(_, { value }) =>
                    setFilters((current) => ({ ...current, quality: value }))
                  }
                />
              </Form.Field>
            </Form.Group>
            <Form.Field>
              <Checkbox
                label={t('vertex_log.user_input_error_only_v820')}
                checked={filters.user_input_error_only}
                onChange={(_, { checked }) =>
                  setFilters((current) => ({
                    ...current,
                    user_input_error_only: checked,
                  }))
                }
              />
            </Form.Field>
            <Button
              primary
              onClick={() => {
                setPage(1);
                setAppliedFilters(filters);
              }}
            >
              {t('vertex_log.apply')}
            </Button>
            <Button
              type='button'
              onClick={() => {
                setFilters({ ...emptyFilters });
                setAppliedFilters({ ...emptyFilters });
                setPage(1);
              }}
            >
              {t('vertex_log.clear')}
            </Button>
          </Form>

          {loading ? (
            <Loader active inline='centered' />
          ) : items.length === 0 ? (
            <Message info content={t('vertex_log.empty')} />
          ) : (
            <div className='vertex-log-table-scroll-v83'>
              <Table
                compact
                selectable
                striped
                unstackable
                className='vertex-log-table-v815'
                style={{ minWidth: tableWidth + 'px' }}
              >
                <colgroup>
                  {columns.map((column) => (
                    <col
                      key={column.key}
                      style={{ width: columnWidths[column.key] + 'px' }}
                    />
                  ))}
                </colgroup>
                <Table.Header>
                  <Table.Row>
                    {columns.map((column) => (
                      <ResizableHeaderCellV815
                        key={column.key}
                        columnKey={column.key}
                        width={columnWidths[column.key]}
                        onResizeStart={startResize}
                        onResetWidth={resetColumn}
                      >
                        {column.label}
                      </ResizableHeaderCellV815>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {items.map((item) => (
                    <Table.Row key={item.id}>
                      <Table.Cell>{formatTime(item.created_at)}</Table.Cell>
                      <Table.Cell>
                        <ProviderAccessCellV817 item={item} t={t} />
                      </Table.Cell>
                      <Table.Cell>
                        <code>{item.request_id}</code>
                      </Table.Cell>
                      <Table.Cell>
                        {item.organization_name_snapshot ||
                          (item.billing_account_id
                            ? '#' + item.billing_account_id
                            : '-')}
                      </Table.Cell>
                      <Table.Cell>
                        {formatNamedIdentityV815(
                          item.username_snapshot,
                          item.user_id
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <div title={item.requested_model || ''}>
                          {item.billed_model || item.requested_model || '-'}
                        </div>
                      </Table.Cell>
                      <Table.Cell>{item.project_reference || '-'}</Table.Cell>
                      <Table.Cell>
                        <CredentialCell
                          keyIndex={item.final_channel_key_index}
                          keyID={item.final_channel_key_id}
                          fingerprint={item.credential_fingerprint}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <div>{item.region || '-'}</div>
                        <small>
                          {item.publisher || '-'} / {item.service_tier || '-'}
                        </small>
                      </Table.Cell>
                      <Table.Cell>
                        <Label
                          basic
                          color={item.outcome === 'success' ? 'green' : 'red'}
                        >
                          {item.outcome || '-'}
                        </Label>
                        <div>HTTP {item.http_status || '-'}</div>
                        {item.error_reason ? (
                          <small title={item.error_message || ''}>
                            {item.error_reason}
                          </small>
                        ) : null}
                      </Table.Cell>
                      <Table.Cell>
                        <div>
                          {t('vertex_log.total_v815')}:{' '}
                          {formatDuration(item.elapsed_ms)}
                        </div>
                        <div>
                          TTFT: {formatDuration(item.first_response_ms, true)}
                        </div>
                        <div>
                          {t('vertex_log.queue_short_v815')}:{' '}
                          {formatDuration(item.queue_wait_ms)}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <QualityCell item={item} t={t} />
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          icon
                          size='mini'
                          title={t('vertex_log.view')}
                          aria-label={t('vertex_log.view')}
                          onClick={() => openDetail(item)}
                        >
                          <Icon name='eye' />
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}

          <div className='list-pagination-v8 vertex-log-pagination-v83'>
            <PageSizeSelectV8
              label={t('log.table.page_size')}
              storageKey='v83-page-size-vertex-logs'
              value={pageSize}
              onChange={(next) => {
                setPage(1);
                setPageSize(next);
              }}
            />
            {total > pageSize ? (
              <Pagination
                activePage={page}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={(_, { activePage }) =>
                  setPage(Number(activePage))
                }
              />
            ) : null}
          </div>
        </>
      )}

      <DetailModalV815
        detail={detail}
        loading={detailLoading}
        onClose={() => setDetail(null)}
        t={t}
      />
    </div>
  );
};

export default VertexLogV83;
